'use client';

import { createContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { useSnackbar } from '@/hooks/useSnackbar';
import {
  cadastrarAssinaturaUsuario,
  recuperarAssinaturasNaoContratadasPeloUsuario,
  recuperarPlataformasStreaming,
  registraConteudosAssistidosStreaming,
} from '@/services/carteira/minhaCarteiraService';
import { AssinaturaManualResumida } from '@/types/assinaturaManual';
import { Plano, PlataformaStreaming } from '@/types/plataformaStreaming';
import { RecorrenciaPlanoAssinaturaEnum } from '@/services/carteira/enums/recorrenciaPlanoAssinaturaEnum';
import * as Yup from 'yup';
import {
  Control,
  useForm,
  UseFormHandleSubmit,
  UseFormRegister,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { breadcrumbMinhaCarteira } from '@/components/pages/MinhaCarteira/breadcrumb/config';
import { FormCadastrarAssinaturaRequest } from '@/services/carteira/types/cadastro_assinatura';
import { APIResponse } from '@/services/types';
import { registrarEvento } from '@/services/googleAnalytics';
import { useSession } from 'next-auth/react';

export type OpcaoRecorrenciaAssinatura = {
  id: RecorrenciaPlanoAssinaturaEnum;
  label: string;
  disponivel: boolean;
};

export type PlanoPersonalizado = {
  nome: string;
  valor: string;
  meses: RecorrenciaPlanoAssinaturaEnum;
};

const mesesArray = Object.values(
  RecorrenciaPlanoAssinaturaEnum,
) as RecorrenciaPlanoAssinaturaEnum[];

export const validationCadastroPlanoSchema = Yup.object().shape({
  nome: Yup.string().required('O nome é obrigatório'),
  valor: Yup.string().required('O valor é obrigatório'),
  meses: Yup.mixed<RecorrenciaPlanoAssinaturaEnum>()
    .oneOf(mesesArray, 'Meses inválido')
    .required('O meses é obrigatório'),
});

export type DadosAssinaturaParaCadastro = {
  plataformaSelecionadaParaCadastro: PlataformaStreaming;
  usuarioEscolheuPlanoPersonalizadoParaCadastro: boolean;
  planoPersonalizadoParaCadastro?: PlanoPersonalizado;
  planoSelecionadoParaCadastro?: Plano;
  conteudosSelecionadosParaCadastro: string[];
};

interface ContextoSelecionarAssinaturaType {
  loadingPlataformas: boolean;
  plataformasStreaming: PlataformaStreaming[];
  assinaturasManuais: AssinaturaManualResumida[];
  plataformaSelecionada: string;
  setPlataformaSelecionada: (id: string) => void;
  opcoesDropdownAssinatura: OpcaoRecorrenciaAssinatura[];
  filtroPlanoPlataforma: RecorrenciaPlanoAssinaturaEnum;
  setFiltroPlanoPlataforma: (filtro: RecorrenciaPlanoAssinaturaEnum) => void;
  planosPlataforma: Plano[];
  setPaginaCadastroAssinatura: (pagina: 1 | 2 | 3) => void;
  paginaCadastroAssinatura: 1 | 2 | 3;
  planoSelecionado: string;
  setPlanoSelecionado: (id: string) => void;
  voltarParaCarteira: () => void;
  planoPersonalizadoSendoCriado: boolean;
  setPlanoPersonalizadoSendoCriado: (valor: boolean) => void;
  // @ts-expect-error Valor Generico
  // eslint-disable-next-line no-undef
  control: Control<T, unknown>;
  // @ts-expect-error Valor Generico
  // eslint-disable-next-line no-undef
  register: UseFormRegister<T>;
  handleSubmit: UseFormHandleSubmit<PlanoPersonalizado, undefined>;
  planoPersonalizadoSelecionado: boolean;
  cadastrarPlanoPersonalizado: (data: PlanoPersonalizado) => void;
  planoPersonalizado: PlanoPersonalizado | undefined;
  selecionarPlanoPersonalizado: () => void;
  limparPlanoPersonalizado: () => void;
  cancelarCriacaoPlanoPersonalizado: () => void;
  cadastrandoAssinaturaUsuario: boolean;
  cadastrarAssinatura: () => Promise<unknown>;
  recuperarAssinaturasUsuario: () => Promise<AssinaturaManualResumida[]>;
  formPlanoCustomizadoEstaValido: boolean;
  resetarContext: () => void;
  conteudosSelecionados: string[];
  selecionarConteudo: (id: string) => void;
  recuperarPlataformas: () => Promise<void>;
  registrarConteudosVistos: ({
    podeMostrarSnackbar,
    tituloIDs,
  }: {
    podeMostrarSnackbar: boolean;
    tituloIDs: string[];
  }) => Promise<APIResponse>;
  setRegistrandoConteudosVistoPeloUsuario: (valor: boolean) => void;
  registrandoConteudosVistoPeloUsuario: boolean;
  removerAssinaturasPendentesCadastro: () => void;
  salvarCadastroAssinaturaParaExecucaoPosterior: () => Promise<boolean>;
  salvandoAssinaturasParaCadastroPosterior: boolean;
  executarCadastroAssinatura: (
    dados: DadosAssinaturaParaCadastro,
  ) => Promise<APIResponse>;
}

export function categorizarPlanos(plataforma: PlataformaStreaming | undefined) {
  if (!plataforma || !plataforma.planos) return { mensal: [], anual: [] };

  const mensal = plataforma.planos.filter(
    (plano) => plano.meses === RecorrenciaPlanoAssinaturaEnum.MENSAL,
  );
  const anual = plataforma.planos.filter(
    (plano) => plano.meses === RecorrenciaPlanoAssinaturaEnum.ANUAL,
  );

  return { mensal, anual };
}

export const ContextCadastrarAssinatura = createContext<
  ContextoSelecionarAssinaturaType | undefined
>(undefined);

export function ProviderCadastrarAssinatura({
  children,
}: {
  children: ReactNode;
}) {
  const { mostrarSnackbar } = useSnackbar();
  const router = useRouter();
  const { status } = useSession();

  const [assinaturasManuais, setAssinaturasManuais] = useState<
    AssinaturaManualResumida[]
  >([]);

  const [loadingPlataformas, setLoadingPlataformas] = useState(false);
  const [plataformasStreaming, setPlataformasStreaming] = useState<
    PlataformaStreaming[]
  >([]);
  const [plataformaSelecionada, setPlataformaSelecionada] =
    useState<string>('');
  const [paginaCadastroAssinatura, setPaginaCadastroAssinatura] = useState<
    1 | 2 | 3
  >(1);
  const [filtroPlanoPlataforma, setFiltroPlanoPlataforma] =
    useState<RecorrenciaPlanoAssinaturaEnum>(1);
  const [planoSelecionado, setPlanoSelecionado] = useState<string>('');
  const [planoPersonalizado, setPlanoPersonalizado] = useState<
    undefined | PlanoPersonalizado
  >(undefined);
  const [planoPersonalizadoSendoCriado, setPlanoPersonalizadoSendoCriado] =
    useState(false);
  const [planoPersonalizadoSelecionado, setPlanoPersonalizadoSelecionado] =
    useState(false);

  const [conteudosSelecionados, setConteudosSelecionados] = useState<string[]>(
    [],
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<PlanoPersonalizado>({
    defaultValues: { nome: undefined },
    resolver: yupResolver(validationCadastroPlanoSchema),
    mode: 'onChange',
  });

  async function recuperarAssinaturasUsuario(): Promise<
    AssinaturaManualResumida[]
  > {
    setLoadingPlataformas(true);

    const mesclagemAssinaturasRealizadasComAssinaturasManuais: AssinaturaManualResumida[] =
      await recuperarAssinaturasNaoContratadasPeloUsuario();

    setAssinaturasManuais(mesclagemAssinaturasRealizadasComAssinaturasManuais);

    setLoadingPlataformas(false);

    return mesclagemAssinaturasRealizadasComAssinaturasManuais;
  }

  async function recuperarPlataformas(): Promise<void> {
    try {
      setLoadingPlataformas(true);
      const plataformas = await recuperarPlataformasStreaming();
      if (status !== 'authenticated') {
        const cadastrosAssinaturasPendentes = localStorage.getItem(
          'cadastrosAssinaturasPendentes',
        );

        if (cadastrosAssinaturasPendentes) {
          const assinaturasSalvas: DadosAssinaturaParaCadastro[] = JSON.parse(
            cadastrosAssinaturasPendentes,
          );

          const plataformasSalvasIDs = assinaturasSalvas.map(
            (a) => a.plataformaSelecionadaParaCadastro?.plataformaStreamingID,
          );

          const plataformasFiltradas = plataformas.filter(
            (p) => !plataformasSalvasIDs.includes(p.plataformaStreamingID),
          );

          setPlataformasStreaming(plataformasFiltradas);
          return;
        }

        setPlataformasStreaming(plataformas);
        return;
      }

      const assinaturasUsuario = await recuperarAssinaturasUsuario();

      if (!assinaturasUsuario.length) {
        setPlataformasStreaming(plataformas);
        return;
      }

      const plataformasDisponiveis = plataformas.filter(
        (p) =>
          !assinaturasUsuario.some(
            (a) => a.plataformaStreamingID === p.plataformaStreamingID,
          ),
      );

      setPlataformasStreaming(plataformasDisponiveis);
    } catch (error: unknown) {
      if (error instanceof Error) {
        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? '',
          size: 1,
          variant: 'solid',
        });
      }
    } finally {
      setLoadingPlataformas(false);
    }
  }

  useEffect(() => {
    if (status !== 'loading' && !plataformasStreaming.length)
      recuperarPlataformas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const opcoesDropdownAssinatura = useMemo(() => {
    if (!plataformasStreaming.length) return [];

    const plataforma = plataformasStreaming.find(
      (p) => p.plataformaStreamingID === plataformaSelecionada,
    );

    const planos = categorizarPlanos(plataforma);

    const opcoes: OpcaoRecorrenciaAssinatura[] = [
      {
        id: RecorrenciaPlanoAssinaturaEnum.MENSAL,
        label: 'Mensal',
        disponivel: planos.mensal.length > 0,
      },
      {
        id: RecorrenciaPlanoAssinaturaEnum.ANUAL,
        label: 'Anual',
        disponivel: planos.anual.length > 0,
      },
    ];

    return opcoes.filter((o) => o.disponivel);
  }, [plataformasStreaming, plataformaSelecionada]);

  const planosPlataforma = useMemo(() => {
    if (!plataformasStreaming.length) return [];

    const plataforma = plataformasStreaming.find(
      (p) => p.plataformaStreamingID === plataformaSelecionada,
    );

    if (!plataforma?.planos?.length) return [];

    return (
      plataforma?.planos.filter(
        (plano) => plano.meses === filtroPlanoPlataforma,
      ) || []
    );
  }, [plataformasStreaming, plataformaSelecionada, filtroPlanoPlataforma]);

  function voltarParaCarteira() {
    setPlataformaSelecionada('');
    setPlanoPersonalizado(undefined);
    setPlanoPersonalizadoSelecionado(false);
    return router.push(breadcrumbMinhaCarteira.href, { scroll: true });
  }

  async function cadastrarPlanoPersonalizado(data: PlanoPersonalizado) {
    setPlanoSelecionado('');
    setPlanoPersonalizadoSelecionado(true);
    setPlanoPersonalizado(data);
  }

  useEffect(() => {
    if (planoSelecionado.length > 2) {
      setPlanoPersonalizadoSelecionado(false);
    }
  }, [planoSelecionado]);

  function selecionarPlanoPersonalizado() {
    setPlanoSelecionado('');
    return setPlanoPersonalizadoSelecionado(true);
  }

  function limparPlanoPersonalizado() {
    setPlanoPersonalizadoSelecionado(false);
    return setPlanoPersonalizado(undefined);
  }

  function cancelarCriacaoPlanoPersonalizado() {
    if (!planoPersonalizado) {
      return reset();
    }
  }

  const [cadastrandoAssinaturaUsuario, setCadastrandoAssinaturaUsuario] =
    useState(false);

  const [
    registrandoConteudosVistoPeloUsuario,
    setRegistrandoConteudosVistoPeloUsuario,
  ] = useState<boolean>(false);

  async function registrarConteudosVistos({
    podeMostrarSnackbar = false,
    tituloIDs,
  }: {
    podeMostrarSnackbar?: boolean;
    tituloIDs: string[];
  }): Promise<APIResponse> {
    try {
      setRegistrandoConteudosVistoPeloUsuario(true);

      const retornoRegistroConteudosAssistidosStreaming =
        await registraConteudosAssistidosStreaming({
          plataformaStreamingID: plataformaSelecionada,
          tituloIDs,
        });

      if (podeMostrarSnackbar) {
        mostrarSnackbar({
          color: 'success',
          highContrast: true,
          mensagem: 'Conteúdos adicionados no seu consumo!',
          size: 1,
          variant: 'solid',
        });
      }

      return retornoRegistroConteudosAssistidosStreaming;
    } catch (error: unknown) {
      if (error instanceof Error && podeMostrarSnackbar) {
        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? '',
          size: 1,
          variant: 'solid',
        });
      }

      return {
        dados: [],
        mensagem: '',
        sucesso: false,
      };
    } finally {
      setRegistrandoConteudosVistoPeloUsuario(false);
    }
  }

  async function executarCadastroAssinatura(
    dados: DadosAssinaturaParaCadastro,
  ) {
    const {
      conteudosSelecionadosParaCadastro,
      plataformaSelecionadaParaCadastro,
      usuarioEscolheuPlanoPersonalizadoParaCadastro,
      planoPersonalizadoParaCadastro,
      planoSelecionadoParaCadastro,
    } = dados;

    setCadastrandoAssinaturaUsuario(true);

    const retornoRegistroConteudos = await registrarConteudosVistos({
      tituloIDs: conteudosSelecionadosParaCadastro,
    });

    if (!retornoRegistroConteudos.sucesso) {
      throw new Error(retornoRegistroConteudos.mensagem);
    }

    const formCadastroAssinatura: FormCadastrarAssinaturaRequest = {
      plataformaStreamingID:
        plataformaSelecionadaParaCadastro.plataformaStreamingID,
    };

    if (
      usuarioEscolheuPlanoPersonalizadoParaCadastro &&
      planoPersonalizadoParaCadastro
    ) {
      formCadastroAssinatura.nome = planoPersonalizadoParaCadastro.nome;
      formCadastroAssinatura.valor = Number(
        planoPersonalizadoParaCadastro.valor,
      ).toFixed(2);
      formCadastroAssinatura.meses = planoPersonalizadoParaCadastro.meses;
    } else if (planoSelecionadoParaCadastro) {
      formCadastroAssinatura.id =
        planoSelecionadoParaCadastro.plataformaStreamingPlanoID;
      formCadastroAssinatura.nome = planoSelecionadoParaCadastro.nome;
      formCadastroAssinatura.valor = Number(
        planoSelecionadoParaCadastro.valor,
      ).toFixed(2);
      formCadastroAssinatura.meses = planoSelecionadoParaCadastro.meses;
    } else {
      throw new Error('Nenhum plano válido foi selecionado');
    }

    const retornoCadastro = await cadastrarAssinaturaUsuario(
      formCadastroAssinatura,
    );
    const { sucesso, mensagem } = retornoCadastro;

    if (!sucesso) throw new Error(mensagem);

    return retornoCadastro;
  }

  async function cadastrarAssinatura(): Promise<unknown> {
    try {
      const plataformaSelecionadaParaCadastro = plataformasStreaming.find(
        (p) => p.plataformaStreamingID === plataformaSelecionada,
      );

      const planoSelecionadoParaCadastro = planosPlataforma.find(
        (p) => p.plataformaStreamingPlanoID === planoSelecionado,
      );

      if (!plataformaSelecionadaParaCadastro)
        throw new Error(
          'Não foi possível recuperar a assinatura selecionada pelo usuário',
        );

      if (!planoSelecionadoParaCadastro && !planoPersonalizado)
        throw new Error(
          'Não foi possível recuperar o plano da assinatura selecionado pelo usuário',
        );

      const nomePlano = planoPersonalizadoSelecionado
        ? planoPersonalizado?.nome
        : planoSelecionadoParaCadastro?.nome;

      const valorPlano = planoPersonalizadoSelecionado
        ? planoPersonalizado?.valor
        : planoSelecionadoParaCadastro?.valor;

      registrarEvento('clique_cadastrar_assinatura', {
        plataforma: plataformaSelecionadaParaCadastro.nome,
        nome: nomePlano,
        valor: valorPlano,
      });

      const retornoRequestCadastroAssinaturaUsuario =
        await executarCadastroAssinatura({
          conteudosSelecionadosParaCadastro: conteudosSelecionados,
          plataformaSelecionadaParaCadastro,
          usuarioEscolheuPlanoPersonalizadoParaCadastro:
            planoPersonalizadoSelecionado,
          planoPersonalizadoParaCadastro: planoPersonalizado,
          planoSelecionadoParaCadastro,
        });

      recuperarAssinaturasUsuario();

      router.replace(breadcrumbMinhaCarteira.href);

      setPlanoSelecionado('');
      setPlanoPersonalizado(undefined);
      setPlataformaSelecionada('');

      return retornoRequestCadastroAssinaturaUsuario;
    } catch (error: unknown) {
      if (error instanceof Error) {
        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? '',
          size: 1,
          variant: 'solid',
        });
      }
    } finally {
      setCadastrandoAssinaturaUsuario(false);
    }
  }

  function selecionarConteudo(tituloID: string) {
    return setConteudosSelecionados((conteudosSelecionadosPreviamente) =>
      conteudosSelecionadosPreviamente.includes(tituloID)
        ? conteudosSelecionadosPreviamente.filter(
            (conteudo) => conteudo !== tituloID,
          )
        : [...conteudosSelecionadosPreviamente, tituloID],
    );
  }

  function resetarContext() {
    reset();
    setPlataformaSelecionada('');
    setPaginaCadastroAssinatura(1);
    setFiltroPlanoPlataforma(1);
    setPlanoSelecionado('');
    setPlanoPersonalizado(undefined);
    setPlanoPersonalizadoSendoCriado(false);
    setPlanoPersonalizadoSelecionado(false);
    setConteudosSelecionados([]);
  }

  const [
    salvandoAssinaturasParaCadastroPosterior,
    setSalvandoAssinaturasParaCadastroPosterior,
  ] = useState(false);

  async function salvarCadastroAssinaturaParaExecucaoPosterior() {
    try {
      setSalvandoAssinaturasParaCadastroPosterior(true);
      const pendentes: DadosAssinaturaParaCadastro[] = JSON.parse(
        localStorage.getItem('cadastrosAssinaturasPendentes') || '[]',
      );

      const plataformaSelecionadaParaCadastro = plataformasStreaming.find(
        (p) => p.plataformaStreamingID === plataformaSelecionada,
      );

      const planoSelecionadoParaCadastro = planosPlataforma.find(
        (p) => p.plataformaStreamingPlanoID === planoSelecionado,
      );

      if (!plataformaSelecionadaParaCadastro)
        throw new Error(
          'Não foi possível recuperar a assinatura selecionada pelo usuário',
        );

      if (!planoSelecionadoParaCadastro && !planoPersonalizado)
        throw new Error(
          'Não foi possível recuperar o plano da assinatura selecionado pelo usuário',
        );

      pendentes.push({
        conteudosSelecionadosParaCadastro: conteudosSelecionados,
        plataformaSelecionadaParaCadastro,
        usuarioEscolheuPlanoPersonalizadoParaCadastro:
          planoPersonalizadoSelecionado,
        planoPersonalizadoParaCadastro: planoPersonalizado,
        planoSelecionadoParaCadastro,
      });

      localStorage.setItem(
        'cadastrosAssinaturasPendentes',
        JSON.stringify(pendentes),
      );

      resetarContext();

      await recuperarPlataformas();

      return true;
    } catch (error: unknown) {
      if (error instanceof Error) {
        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? '',
          size: 1,
          variant: 'solid',
        });
      }
      return false;
    } finally {
      setSalvandoAssinaturasParaCadastroPosterior(false);
    }
  }

  function removerAssinaturasPendentesCadastro() {
    return localStorage.removeItem('cadastrosAssinaturasPendentes');
  }

  const valoresContexto: ContextoSelecionarAssinaturaType = {
    loadingPlataformas,
    plataformasStreaming,
    assinaturasManuais,
    plataformaSelecionada,
    setPlataformaSelecionada,
    opcoesDropdownAssinatura,
    filtroPlanoPlataforma,
    setFiltroPlanoPlataforma,
    planosPlataforma,
    setPaginaCadastroAssinatura,
    paginaCadastroAssinatura,
    planoSelecionado,
    setPlanoSelecionado,
    voltarParaCarteira,
    planoPersonalizadoSendoCriado,
    setPlanoPersonalizadoSendoCriado,
    control,
    register,
    handleSubmit,
    planoPersonalizado,
    planoPersonalizadoSelecionado,
    cadastrarPlanoPersonalizado,
    selecionarPlanoPersonalizado,
    limparPlanoPersonalizado,
    cancelarCriacaoPlanoPersonalizado,
    cadastrandoAssinaturaUsuario,
    cadastrarAssinatura,
    recuperarAssinaturasUsuario,
    formPlanoCustomizadoEstaValido: isValid,
    resetarContext,
    conteudosSelecionados,
    selecionarConteudo,
    recuperarPlataformas,
    registrarConteudosVistos,
    setRegistrandoConteudosVistoPeloUsuario,
    registrandoConteudosVistoPeloUsuario,
    salvarCadastroAssinaturaParaExecucaoPosterior,
    removerAssinaturasPendentesCadastro,
    salvandoAssinaturasParaCadastroPosterior,
    executarCadastroAssinatura,
  };

  return (
    <ContextCadastrarAssinatura.Provider value={valoresContexto}>
      {children}
    </ContextCadastrarAssinatura.Provider>
  );
}
